import React, { useMemo } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'src/arb-wrapper';
import type { FormRhfControllerProps } from 'src/components/common/forms/types';
import { FormProductStrategy } from 'src/components/campaigns/forms/FormProductStrategy';
import { type CampaignPublishedStateDto, type Country, type ProductStrategy } from 'src/store/generated/leela-api-generated';
import { groupStrategiesByAbTesting, getProductSetsForGroup } from 'src/utils/abTestingUtils';
import { CampaignPublishers } from 'src/store/constants';

// Deep-clone plain (JSON-serializable) strategy config so the new strategy shares no nested
// references with the source.
function deepClone<T> (value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

interface FormProductStrategiesProps extends FormRhfControllerProps {
  publisher: CampaignPublishedStateDto['publisher'];
  isEditing?: boolean;
  disableAdd?: boolean;
  allowedCountries?: string[];
  allowedBusinessLines?: string[];
  campaignCountry?: string;
  campaignBusinessLine?: string;
  /** Countries offered by the publisher's account configs; used when no config narrows the list. */
  publisherCountries: readonly Country[];
  /** Countries the user's MANAGE_CAMPAIGNS scope allows; omitted means unrestricted. */
  scopeCountries?: readonly string[];
  publisherTimezone: string;
}

export const FormProductStrategies: React.FC<FormProductStrategiesProps> = ({
  control,
  name,
  publisher,
  isEditing = false,
  disableAdd = false,
  allowedCountries,
  allowedBusinessLines,
  campaignCountry,
  campaignBusinessLine,
  publisherCountries,
  scopeCountries,
  publisherTimezone,
}) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
    rules: { required: true },
  });

  // Watch the actual product strategies data to get groupId
  const watchedStrategies = useWatch({
    control,
    name,
  });

  // Group strategies by A/B testing groupId
  const { abTestingGroups, ungroupedStrategies } = useMemo(() => {
    const productStrategies = (watchedStrategies as ProductStrategy[]) || [];
    const result = groupStrategiesByAbTesting(productStrategies);
    // Sort both groups by original array index so strategies render in stable order
    return {
      abTestingGroups: result.abTestingGroups.sort((a, b) => a.representativeIndex - b.representativeIndex),
      ungroupedStrategies: result.ungroupedStrategies.sort((a, b) => a.index - b.index),
    };
  }, [watchedStrategies]);

  // Only Google campaigns support more than one product strategy. Other publishers are capped at 1.
  // Delete is allowed whenever there are multiple strategies (regardless of publisher).
  const allowsMultipleStrategies = publisher === CampaignPublishers.GOOGLE;
  const canAddStrategy = allowsMultipleStrategies || fields.length < 1;
  const canRemoveStrategy = fields.length > 1;
  const totalRenderedStrategyCards = abTestingGroups.length + ungroupedStrategies.length;
  const isOnlyStrategy = totalRenderedStrategyCards === 1;

  // Persisted once it has a productStrategyId; newly-added drafts don't.
  const isStrategyPersisted = (index: number): boolean =>
    !!(watchedStrategies as Array<{ productStrategyId?: string }> | undefined)?.[index]?.productStrategyId;

  const handleAddProductStrategy = () => {
    const existing = (watchedStrategies as ProductStrategy[] | undefined) ?? [];
    const last = existing[existing.length - 1];

    if (last) {
      // Clone the previous strategy, dropping identity/server-stamped fields and title source.
      const {
        productStrategyId: _psId,
        name: _name,
        productSetId: _psSet,
        groupId: _groupId,
        abTestingEnabled: _ab,
        abTestingVariations: _abv,
        customCatalogConfig: _catalog,
        pageId: _pageId,
        strategyId: _strategyId,
        gtiList: _gtiList,
        benderTemplates,
        ...clonable
      } = last as ProductStrategy & Record<string, unknown>;
      // Deep-clone so the new strategy shares no nested references with the source.
      // Strip each template's server-stamped templateConfigId.
      const freshTemplates = (benderTemplates ?? []).map(({ templateConfigId: _tcId, ...tmpl }) => deepClone(tmpl));
      append({ ...deepClone(clonable), benderTemplates: freshTemplates, name: undefined, productSetId: '' });
      return;
    }

    append({
      benderTemplates: [],
      businessLine: 'SVOD',
      country: null,
      locales: [],
      pageId: undefined,
      productSetId: '',
      strategyId: undefined,
    });
  };

  const handleDeleteProductStrategy = (index: number) => {
    remove(index);
  };

  const handleDeleteGroup = (groupIndices: number[]) => {
    // Remove all strategies in the group, starting from the highest index
    // to avoid index shifting issues
    groupIndices
      .sort((a, b) => b - a)
      .forEach(index => {
        remove(index);
      });
  };

  return (
    <Column>
      <Row alignmentHorizontal="justify">
        <Heading level={3}>{t('form_product_strategies_header')}</Heading>
        <div hidden={!canAddStrategy}>
          <Button
            data-testid="add-product-strategy-button"
            disabled={disableAdd}
            size="small"
            type="tertiary"
            onClick={handleAddProductStrategy}
          >
            <Icon tokens={plusTokens} />
            {t('form_add_strategy')}
          </Button>
        </div>
      </Row>

      <Column spacingInset="none none none 400">
        {fields.length === 0 && (
          <Text data-testid="product-strategy-error-message">
            {t('empty_list_error_message')}
          </Text>
        )}

        {/* Render A/B Testing Groups */}
        {abTestingGroups.map((group) => {
          const groupIndices = group.strategies.map(s => s.index);
          const productSets = getProductSetsForGroup(group.strategies);

          return (
            <FormProductStrategy
              allowedBusinessLines={allowedBusinessLines}
              allowedCountries={allowedCountries}
              campaignBusinessLine={campaignBusinessLine}
              campaignCountry={campaignCountry}
              canRemove={canRemoveStrategy}
              control={control}
              index={group.representativeIndex}
              isAbTestingGroup={true}
              isEditing={isEditing}
              isOnlyStrategy={isOnlyStrategy}
              isPersisted={isEditing && isStrategyPersisted(group.representativeIndex)}
              key={`group-${group.groupId}`}
              name={name}
              productSets={productSets}
              publisher={publisher}
              publisherCountries={publisherCountries}
              publisherTimezone={publisherTimezone}
              scopeCountries={scopeCountries}
              variationCount={group.strategies.length}
              onDelete={() => {
                handleDeleteGroup(groupIndices);
              }}
            />
          );
        })}

        {/* Render Ungrouped Strategies */}
        {ungroupedStrategies.map(({ index }) => {
          const field = fields[index];
          if (!field) return null;

          return (
            <FormProductStrategy
              allowedBusinessLines={allowedBusinessLines}
              allowedCountries={allowedCountries}
              campaignBusinessLine={campaignBusinessLine}
              campaignCountry={campaignCountry}
              canRemove={canRemoveStrategy}
              control={control}
              index={index}
              isEditing={isEditing}
              isOnlyStrategy={isOnlyStrategy}
              isPersisted={isEditing && isStrategyPersisted(index)}
              key={field.id}
              name={name}
              publisher={publisher}
              publisherCountries={publisherCountries}
              publisherTimezone={publisherTimezone}
              scopeCountries={scopeCountries}
              onDelete={() => {
                handleDeleteProductStrategy(index);
              }}
            />
          );
        })}
      </Column>
    </Column>
  );
};
